import React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { SkeletonLines, SkeletonRows } from '@/components/Admin/UI/Skeleton';
import { colors } from '@/constants/colors';
import { currencyFormat } from '@/utils/currencyFormatter';
import { useUIStore } from '@/store/useUIStore';
import { getMyCoinsLiveApi, type CoinRow } from '@/api/vendo';
import { getAdminCoinsLive, type AdminCoinRow } from '@/api/admin';
import { useTeamStore } from '@/store/useTeamStore';

const POLL_MS = 10_000;

// ── shared helpers ──────────────────────────────────────────────────────────
function useCountUp(target: number, durationMs = 800): number {
   const [val, setVal] = useState(target);
   const ref = useRef(target);
   useEffect(() => {
      const from = ref.current;
      if (from === target) return;
      let raf = 0;
      const start = performance.now();
      const step = (t: number) => {
         const p = Math.min(1, (t - start) / durationMs);
         const eased = 1 - Math.pow(1 - p, 3);
         const v = Math.round(from + (target - from) * eased);
         ref.current = v;
         setVal(v);
         if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
      return () => cancelAnimationFrame(raf);
   }, [target, durationMs]);
   return val;
}

// Pulses true briefly whenever `value` increases — used to pop the running
// total so a new coin is obvious even from across the room.
function usePop(value: number): boolean {
   const [pop, setPop] = useState(false);
   const prev = useRef(value);
   useEffect(() => {
      if (value > prev.current) {
         setPop(false);
         const raf = requestAnimationFrame(() => setPop(true));
         const t = setTimeout(() => setPop(false), 650);
         prev.current = value;
         return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t);
         };
      }
      prev.current = value;
   }, [value]);
   return pop;
}

function ago(iso: string, nowMs: number): string {
   const secs = Math.max(0, Math.round((nowMs - new Date(iso).getTime()) / 1000));
   if (secs < 5) return 'just now';
   if (secs < 60) return `${secs}s ago`;
   const mins = Math.floor(secs / 60);
   if (mins < 60) return `${mins}m ago`;
   const hrs = Math.floor(mins / 60);
   if (hrs < 24) return `${hrs}h ago`;
   return `${Math.floor(hrs / 24)}d ago`;
}

// The real coin-drop sound (same clip the captive portal uses). Played only
// when the operator turns sound on (default off) and only for coins that arrive
// after mount. One shared element, rewound each time so rapid coins retrigger.
const coinAudio =
   typeof Audio !== 'undefined' ? new Audio('/audio/coin1.mp3') : null;
function playCoin() {
   if (!coinAudio) return;
   try {
      coinAudio.currentTime = 0;
      // Browsers refuse audio before the first click on the page; a refused
      // chime is not an error worth an uncaught promise in the console.
      coinAudio.play().catch(() => {});
   } catch {
      /* autoplay blocked until a user gesture — silent, no-op */
   }
}

const KindBadge = ({ kind }: { kind: 'new' | 'extend' | null }) => {
   if (!kind) return null;
   return kind === 'new' ? (
      <Badge $new>New</Badge>
   ) : (
      <Badge>Extend</Badge>
   );
};

const maskNone = (mac: string | null) => mac || '—';

// Sound on/off, remembered across refreshes and shared by both tickers.
const SOUND_KEY = 'smartfi-coin-ticker-sound';
function useSoundToggle(): [boolean, () => void] {
   const [on, setOn] = useState(() => {
      try {
         return localStorage.getItem(SOUND_KEY) === '1';
      } catch {
         return false;
      }
   });
   const toggle = () =>
      setOn((v) => {
         const next = !v;
         try {
            localStorage.setItem(SOUND_KEY, next ? '1' : '0');
         } catch {
            /* ignore */
         }
         return next;
      });
   return [on, toggle];
}

// Clear on/off affordance: a labelled pill, muted-speaker icon + "Off" when off.
const SoundToggle = ({ on, onClick }: { on: boolean; onClick: () => void }) => (
   <SoundBtn
      type="button"
      $on={on}
      onClick={onClick}
      title={on ? 'Coin sound on — click to mute' : 'Coin sound off — click to enable'}
      aria-pressed={on}
   >
      <span>{on ? '🔊' : '🔇'}</span>
      <SoundLbl>{on ? 'On' : 'Off'}</SoundLbl>
   </SoundBtn>
);

// ── Operator card ───────────────────────────────────────────────────────────
export const CoinTicker = () => {
   const { handleShow } = useUIStore();
   const [soundOn, toggleSound] = useSoundToggle();
   const lastTopId = useRef<string | null>(null);
   const mounted = useRef(false);
   const [nowMs, setNowMs] = useState(Date.now());

   const { data } = useQuery({
      queryKey: ['my-coins-live'],
      queryFn: getMyCoinsLiveApi,
      refetchInterval: POLL_MS,
      refetchOnWindowFocus: true,
   });

   // Re-tick "x ago" labels every 15s without refetching.
   useEffect(() => {
      const t = setInterval(() => setNowMs(Date.now()), 15_000);
      return () => clearInterval(t);
   }, []);

   const coins: CoinRow[] = data?.recentCoins ?? [];
   const total = useCountUp(data?.coinsToday ?? 0);
   const count = useCountUp(data?.coinCountToday ?? 0);
   const bumped = usePop(data?.coinsToday ?? 0);

   // Sound + highlight only for coins that land after the card mounted.
   useEffect(() => {
      const topId = coins[0]?.id ?? null;
      if (mounted.current && topId && topId !== lastTopId.current && soundOn) {
         playCoin();
      }
      lastTopId.current = topId;
      mounted.current = true;
   }, [coins, soundOn]);

   return (
      <Card>
         <Head>
            <div>
               <Kicker>Coins today</Kicker>
               <Total $pop={bumped}>{currencyFormat(total, false)}</Total>
               <Sub>{count.toLocaleString()} coin{count === 1 ? '' : 's'} · live</Sub>
            </div>
            <HeadActions>
               <SoundToggle on={soundOn} onClick={toggleSound} />
               <ViewAllBtn
                  type="button"
                  onClick={() =>
                     handleShow({
                        template: 'coins-all',
                        title: 'Coins today',
                        windowType: 'desktop',
                        data: { source: 'operator' },
                     })
                  }
               >
                  View all
               </ViewAllBtn>
            </HeadActions>
         </Head>

         <Grid>
            <GridHead>
               <span>Device</span>
               <span>Customer MAC</span>
               <span className="r">Time</span>
               <span className="r">Amount</span>
            </GridHead>
            {!data && <SkeletonLines lines={5} />}
            {data && coins.length === 0 && <Empty>No coins yet today.</Empty>}
            {coins.map((c, i) => (
               <GridRow key={c.id} $fresh={i === 0}>
                  <DevName>{c.deviceName}</DevName>
                  <MacText>{maskNone(c.mac)}</MacText>
                  <TimeText>{ago(c.at, nowMs)}</TimeText>
                  <AmtCell>
                     {c.minutes > 0 && <Mins>+{c.minutes}m</Mins>}
                     <KindBadge kind={c.kind} />
                     <Amt>{currencyFormat(c.amount, false)}</Amt>
                  </AmtCell>
               </GridRow>
            ))}
         </Grid>
      </Card>
   );
};

// ── Admin fleet card ────────────────────────────────────────────────────────
export const AdminCoinTicker = () => {
   const { handleShow } = useUIStore();
   const [soundOn, toggleSound] = useSoundToggle();
   const lastTopId = useRef<string | null>(null);
   const mounted = useRef(false);
   const [nowMs, setNowMs] = useState(Date.now());

   // Admin routes are refused while helping an operator — do not poll them.
   const acting = useTeamStore((s) => s.acting);
   const { data } = useQuery({
      queryKey: ['admin-coins-live'],
      queryFn: getAdminCoinsLive,
      enabled: !acting,
      refetchInterval: acting ? false : POLL_MS,
      refetchOnWindowFocus: true,
   });

   useEffect(() => {
      const t = setInterval(() => setNowMs(Date.now()), 15_000);
      return () => clearInterval(t);
   }, []);

   const coins: AdminCoinRow[] = data?.recentCoins ?? [];
   const total = useCountUp(data?.coinsToday ?? 0);
   const count = useCountUp(data?.coinCountToday ?? 0);
   const bumped = usePop(data?.coinsToday ?? 0);

   useEffect(() => {
      const topId = coins[0]?.id ?? null;
      if (mounted.current && topId && topId !== lastTopId.current && soundOn) {
         playCoin();
      }
      lastTopId.current = topId;
      mounted.current = true;
   }, [coins, soundOn]);

   return (
      <Card>
         <Head>
            <div>
               <Kicker>Fleet coins today</Kicker>
               <Total $pop={bumped}>{currencyFormat(total, false)}</Total>
               <Sub>
                  {count.toLocaleString()} coin{count === 1 ? '' : 's'} · all vendos · live
               </Sub>
            </div>
            <HeadActions>
               <SoundToggle on={soundOn} onClick={toggleSound} />
               <ViewAllBtn
                  type="button"
                  onClick={() =>
                     handleShow({
                        template: 'coins-all',
                        title: 'Fleet coins today',
                        windowType: 'desktop',
                        data: { source: 'admin' },
                     })
                  }
               >
                  View all
               </ViewAllBtn>
            </HeadActions>
         </Head>

         <TableScroll>
            <Table>
               <thead>
                  <tr>
                     <Th>Time</Th>
                     <Th>Customer MAC</Th>
                     <Th $right>Amount</Th>
                     <Th>Device</Th>
                     <Th>Type</Th>
                     <Th>Operator</Th>
                     <Th>ISP</Th>
                     <Th />
                  </tr>
               </thead>
               <tbody>
                  {!data && <SkeletonRows rows={5} cols={8} Cell={Td} />}
                  {data && coins.length === 0 && (
                     <tr>
                        <Td colSpan={8}>
                           <Empty>No coins yet today.</Empty>
                        </Td>
                     </tr>
                  )}
                  {coins.map((c, i) => (
                     <BodyRow key={c.id} $fresh={i === 0}>
                        <Td>{ago(c.at, nowMs)}</Td>
                        <Td>
                           <MacText>{maskNone(c.mac)}</MacText>
                        </Td>
                        <Td $right>
                           {c.minutes > 0 && <Mins>+{c.minutes}m</Mins>}{' '}
                           <Amt>{currencyFormat(c.amount, false)}</Amt>
                        </Td>
                        <Td>{c.deviceName}</Td>
                        <Td>
                           {c.type === 'main'
                              ? 'Main'
                              : c.vlanId != null
                              ? `Sub · VLAN ${c.vlanId}`
                              : 'Sub'}
                        </Td>
                        <Td>{c.operator}</Td>
                        <Td>{c.isp || '—'}</Td>
                        <Td>
                           <KindBadge kind={c.kind} />
                        </Td>
                     </BodyRow>
                  ))}
               </tbody>
            </Table>
         </TableScroll>
      </Card>
   );
};

// ── styles ──────────────────────────────────────────────────────────────────
const Card = styled.div`
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY14};
   border-radius: 12px;
   padding: 16px 18px;
`;
const Head = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 12px;
`;
const HeadActions = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;
const Kicker = styled.div`
   font-size: 12px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   color: ${colors.GRAY3};
`;
const pop = keyframes`
   0% { transform: scale(1); }
   30% { transform: scale(1.18); color: ${colors.SUCCESS}; }
   60% { transform: scale(0.98); }
   100% { transform: scale(1); }
`;
const Total = styled.div<{ $pop?: boolean }>`
   font-size: 30px;
   font-weight: 800;
   line-height: 1.1;
   color: ${colors.MAIN_DARK};
   transform-origin: left center;
   animation: ${({ $pop }) => ($pop ? pop : 'none')} 0.6s ease;
`;
const Sub = styled.div`
   font-size: 12px;
   color: ${colors.GRAY3};
   margin-top: 2px;
`;
const ViewAllBtn = styled.button`
   padding: 6px 12px;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.WHITE};
   background: ${colors.PRIMARY};
   border: none;
   border-radius: 8px;
   cursor: pointer;
   &:hover {
      opacity: 0.92;
   }
`;
const SoundBtn = styled.button<{ $on: boolean }>`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   height: 30px;
   padding: 0 10px;
   font-size: 14px;
   border-radius: 8px;
   border: 1px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $on }) => ($on ? `${colors.PRIMARY}14` : colors.GRAY14)};
   cursor: pointer;
   opacity: ${({ $on }) => ($on ? 1 : 0.7)};
   span:first-child {
      line-height: 1;
   }
`;
const SoundLbl = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY3};
`;
const flash = keyframes`
   from { background: ${colors.PRIMARY}1f; }
   to { background: transparent; }
`;
// Balanced columns so device / MAC / time / amount line up instead of
// hugging the two edges with a gap in the middle.
const GRID_COLS = 'minmax(0, 1.3fr) minmax(0, 1.5fr) 84px minmax(150px, auto)';
const Grid = styled.div`
   margin-top: 12px;
`;
const GridHead = styled.div`
   display: grid;
   grid-template-columns: ${GRID_COLS};
   gap: 12px;
   padding: 6px 6px;
   border-bottom: 1px solid ${colors.GRAY14};
   span {
      font-size: 11px;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: ${colors.GRAY3};
   }
   span.r {
      text-align: right;
   }
`;
const GridRow = styled.div<{ $fresh?: boolean }>`
   display: grid;
   grid-template-columns: ${GRID_COLS};
   gap: 12px;
   align-items: center;
   padding: 9px 6px;
   border-bottom: 1px solid ${colors.GRAY14};
   animation: ${({ $fresh }) => ($fresh ? flash : 'none')} 1.2s ease both;
   &:last-child {
      border-bottom: none;
   }
`;
const DevName = styled.span`
   font-size: 14px;
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;
const MacText = styled.span`
   font-size: 12px;
   font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
   color: ${colors.GRAY3};
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
`;
const AmtCell = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: 8px;
`;
const Mins = styled.span`
   font-size: 12px;
   color: ${colors.SUCCESS};
   font-weight: 600;
`;
const Amt = styled.span`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const TimeText = styled.span`
   font-size: 12px;
   color: ${colors.GRAY3};
   text-align: right;
   white-space: nowrap;
`;
const Badge = styled.span<{ $new?: boolean }>`
   font-size: 11px;
   font-weight: 700;
   padding: 2px 7px;
   border-radius: 999px;
   color: ${({ $new }) => ($new ? colors.SUCCESS : colors.PRIMARY)};
   background: ${({ $new }) =>
      $new ? `${colors.SUCCESS}1f` : `${colors.PRIMARY}1f`};
`;
const Empty = styled.div`
   padding: 18px 6px;
   font-size: 13px;
   color: ${colors.GRAY3};
   text-align: center;
`;
const TableScroll = styled.div`
   margin-top: 12px;
   overflow-x: auto;
`;
const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
`;
const Th = styled.th<{ $right?: boolean }>`
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   font-size: 11px;
   letter-spacing: 0.03em;
   text-transform: uppercase;
   color: ${colors.GRAY3};
   padding: 6px 10px;
   border-bottom: 1px solid ${colors.GRAY14};
   white-space: nowrap;
`;
const Td = styled.td<{ $right?: boolean }>`
   text-align: ${({ $right }) => ($right ? 'right' : 'left')};
   padding: 8px 10px;
   border-bottom: 1px solid ${colors.GRAY14};
   white-space: nowrap;
   color: ${colors.MAIN_DARK};
`;
const BodyRow = styled.tr<{ $fresh?: boolean }>`
   animation: ${({ $fresh }) => ($fresh ? flash : 'none')} 1.2s ease both;
`;
